import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import HeroKeyvisual from './HeroKeyvisual';
import { riseIn } from '../lib/scrollMotion';
import { scrollToSection } from '../lib/sectionNavigation';

const BASE = process.env.PUBLIC_URL || '';
const HERO_LAYER = `${BASE}/static/css/assets/img/ADX_Hub_Header_Keyvisual_Layer_transparent.png`;

const HeroSection = () => {
  return (
    <section
      id="hero"
      className="relative flex min-h-screen min-h-[100dvh] w-full min-w-0 flex-col items-center overflow-hidden bg-[#0028CD] text-white"
      aria-label="Hero"
    >
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-8%,rgba(255,255,255,0.12),transparent_34%),radial-gradient(circle_at_50%_20%,rgba(0,0,0,0.16),transparent_30%)]" />
        <img
          src={HERO_LAYER}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 hidden h-full w-full object-contain object-top opacity-[0.2] sm:block sm:opacity-[0.3] md:opacity-[0.38]"
          draggable="false"
        />
      </div>

      <div
        className="relative z-10 flex w-full min-w-0 flex-col items-center px-4 pb-12 pt-[calc(5.2rem+env(safe-area-inset-top))] sm:px-6 sm:pb-14 sm:pt-24 md:min-h-[100dvh] md:px-12 md:pb-10"
        style={{ paddingLeft: 'max(1rem, env(safe-area-inset-left))', paddingRight: 'max(1rem, env(safe-area-inset-right))' }}
      >
        <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col items-center justify-end pb-6 pt-10 text-center sm:pb-8 sm:pt-12 md:justify-center md:pb-0 md:pt-0">
          <motion.h1
            className="flex max-w-4xl flex-col items-center text-center tracking-[-0.03em]"
            {...riseIn(0, 60, 0.3)}
          >
            <span className="block text-[clamp(2.05rem,10.2vw,4.25rem)] font-semibold leading-[1.06] text-white sm:text-[clamp(2.5rem,6vw,4.25rem)]">Asset Data</span>
            <span className="block text-[clamp(1.98rem,9.8vw,4.25rem)] font-semibold leading-[1.06] text-[#FF500F] sm:text-[clamp(2.5rem,6vw,4.25rem)]">eXchange</span>
            <span className="block text-[clamp(2.05rem,10.2vw,4.25rem)] font-semibold leading-[1.06] text-white sm:text-[clamp(2.5rem,6vw,4.25rem)]">Hub</span>
          </motion.h1>

          <motion.div
            className="mt-5 w-full max-w-[24rem] border-l-[3px] border-[#FF500F] pl-3.5 text-left sm:mt-4 sm:max-w-2xl sm:border-l-4 sm:pl-4"
            {...riseIn(0.08, 60, 0.3)}
          >
            <p className="text-[0.88rem] leading-[1.58] text-white/92 sm:text-base sm:leading-[1.7]">
              <span className="font-semibold text-white">ADX Hub</span> is a shared-services platform operated by IDTA for AAS-based data exchange, combining identity, trust, partner and AAS endpoint discovery, AAS validation, a Submodel Template Repository, and an AAS editor. It enables providers to publish AAS endpoints, while consumers can discover them, authenticate, and exchange data directly with providers.
            </p>
          </motion.div>

          <motion.div
            className="mt-6 flex w-full max-w-[24rem] flex-col items-stretch gap-3 sm:mt-8 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4"
            {...riseIn(0.16, 60, 0.3)}
          >
            <button
              type="button"
              onClick={() => scrollToSection('services')}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-white/75 bg-white/[0.06] px-7 text-sm font-semibold text-white transition-all hover:bg-white/[0.12] hover:border-white sm:h-11 sm:w-auto"
            >
              Get Started
              <ArrowRight size={18} />
            </button>
            <button
              type="button"
              onClick={() => scrollToSection('how-it-works')}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-white/75 bg-white/[0.06] px-8 text-sm font-semibold text-white transition-all hover:bg-white/[0.12] hover:border-white sm:h-11 sm:w-auto"
            >
              Learn More
              <ArrowRight size={18} />
            </button>
          </motion.div>

          <motion.div
            className="mx-auto mt-10 hidden w-full max-w-5xl rounded-[1.35rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.36),rgba(255,255,255,0.09)_45%,rgba(255,255,255,0.22))] p-[1px] shadow-[0_28px_90px_rgba(0,12,90,0.26)] md:mt-12 md:block"
            {...riseIn(0.24, 70, 0.2)}
          >
            <div className="relative overflow-hidden rounded-[1.28rem] bg-[#0028CD]">
              <div className="pointer-events-none absolute inset-0 z-10 rounded-[1.28rem] ring-1 ring-inset ring-white/[0.13] shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]" />
              <div className="aspect-[2273/1296] w-full bg-[#0028CD]">
                <HeroKeyvisual />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
